/**
 * ReviewLaunchStep -- Create Test Wizard Final Step
 * Step 9F Green Phase
 */

import React, { useState } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Loader2, CheckCircle, Lock, Globe } from "lucide-react";
import { formatInTimezone, formatLocalDateTime } from "../../lib/dateFormat";

export interface ReviewLaunchStepWizardData {
  version?: { id?: string; version_name?: string; [key: string]: unknown };
  scenario?: number | string;
  scenario_name?: string;
  scenario_description?: string;
  testName?: string;
  name?: string;
  description?: string;
  client_company_id?: string;
  client_manager_id?: string;
  dueDate?: string;
  due_date?: string;
  participantCount?: number;
  participants?: Array<{ email: string; name?: string; [key: string]: unknown }>;
  back_navigation_enabled?: boolean;
  min_option_points_threshold?: number;
  is_baseline_source?: boolean;
  baseline_name?: string;
  comparison_disclosure?: boolean;
  version_name?: string;
  [key: string]: unknown;
}

export interface ReviewLaunchStepProps {
  wizardData: ReviewLaunchStepWizardData;
  onBack: () => void;
  // On the final step this resolves to whether the launch succeeded, so the
  // step can re-enable its button on failure; earlier steps return void.
  onNext?: (data?: Record<string, unknown>) => void | Promise<boolean | void>;
}

// Avatar helper
const AVATAR_COLORS = [
  "bg-amber-100 text-amber-700",
  "bg-green-100 text-green-700",
  "bg-blue-100 text-blue-700",
  "bg-purple-100 text-purple-700",
  "bg-red-100 text-red-700",
  "bg-teal-100 text-teal-700",
];

function getInitials(name: string) {
  return name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function getAvatarColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function ReviewLaunchStep({ wizardData, onNext, onBack }: ReviewLaunchStepProps) {
  const [isLaunching, setIsLaunching] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const versionName = wizardData.version?.version_name ?? wizardData.version_name;
  const testName = wizardData.testName ?? wizardData.name;
  const dueDate = wizardData.dueDate ?? wizardData.due_date;
  const formattedDueDate = dueDate ? formatLocalDateTime(dueDate) : undefined;
  const participants = Array.isArray(wizardData.participants) ? wizardData.participants : [];
  const backNav = wizardData.back_navigation_enabled;
  const scenarioNum = Number(wizardData.scenario);

  const handleLaunch = async () => {
    setIsLaunching(true);
    setConfirmOpen(false);
    const launched = await onNext?.(wizardData);
    // On failure the parent stays in the wizard (no navigation), so re-enable
    // the button for a retry. On success the page navigates away and this
    // component unmounts, so we intentionally leave the spinner up.
    if (launched === false) {
      setIsLaunching(false);
    }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Review &amp; Launch</h2>
      <p className="text-sm text-brand-steel mb-6">
        Review all details before launching the assessment. You can go back to any step to make changes.
      </p>

      {/* Test Summary */}
      <Card className="border-brand-steel/50 shadow-sm mb-4">
        <CardContent className="py-6 px-8">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Test Summary</h3>

          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <div>
              <p className="text-xs font-semibold text-brand-steel/70 uppercase tracking-wider mb-1">Test Name</p>
              <p className="text-sm font-medium text-gray-900">{testName ?? "—"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-brand-steel/70 uppercase tracking-wider mb-1">Scenario</p>
              <p className="text-sm font-medium text-gray-900">{wizardData.scenario_name ?? "—"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-brand-steel/70 uppercase tracking-wider mb-1">Content Version</p>
              <p className="text-sm font-medium text-gray-900">{versionName ?? "—"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-brand-steel/70 uppercase tracking-wider mb-1">Due Date</p>
              <p className="text-sm font-medium text-gray-900">{formattedDueDate ?? "—"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-brand-steel/70 uppercase tracking-wider mb-1">Participants</p>
              <p className="text-sm font-medium text-gray-900">
                {participants.length > 0 ? `${participants.length} candidates` : "—"}
              </p>
            </div>
            {wizardData.is_baseline_source && wizardData.baseline_name && (
              <div>
                <p className="text-xs font-semibold text-brand-steel/70 uppercase tracking-wider mb-1">Baseline Source</p>
                <p className="text-sm font-medium text-gray-900">{wizardData.baseline_name}</p>
              </div>
            )}
            {/* Full width — prose needs both columns of the grid. */}
            {wizardData.description && (
              <div className="col-span-2">
                <p className="text-xs font-semibold text-brand-steel/70 uppercase tracking-wider mb-1">Description</p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{wizardData.description}</p>
              </div>
            )}
          </div>

          {/* Candidate List */}
          {participants.length > 0 && (
            <>
              <Separator className="my-5" />
              <p className="text-xs font-semibold text-brand-steel/70 uppercase tracking-wider mb-3">Candidate List</p>
              <div className="space-y-2">
                {participants.map((p, i) => {
                  const email = p.email ?? "";
                  const displayName = p.name ?? email.split("@")[0];
                  const tz = (p.timezone as string) || "";
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold shrink-0 ${getAvatarColor(
                          email
                        )}`}
                        aria-hidden="true"
                      >
                        {getInitials(displayName)}
                      </span>
                      <span className="text-sm font-medium text-gray-900">{displayName}</span>
                      <span className="text-sm text-brand-steel">{email}</span>
                      {dueDate && tz && (
                        <span className="ml-auto flex items-center gap-1 text-xs text-brand-steel">
                          <Globe size={12} aria-hidden="true" />
                          {formatInTimezone(dueDate, tz, false)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Configuration */}
          <Separator className="my-5" />
          <p className="text-xs font-semibold text-brand-steel/70 uppercase tracking-wider mb-3">Configuration</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle size={16} className="text-brand-success shrink-0" aria-hidden="true" />
              <span className="text-sm text-gray-700">Candidate invitations will be sent on launch</span>
            </div>
            {backNav && (
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-brand-success shrink-0" aria-hidden="true" />
                <span className="text-sm text-gray-700">Back navigation enabled for candidates</span>
              </div>
            )}
            {(scenarioNum === 4 || scenarioNum === 5) && (
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-brand-success shrink-0" aria-hidden="true" />
                <span className="text-sm text-gray-700">Client Manager will be notified to define Role Time Prioritization</span>
              </div>
            )}
            {scenarioNum === 4 && (
              <div className="flex items-center gap-2">
                <Lock size={16} className="text-brand-active shrink-0" aria-hidden="true" />
                <span className="text-sm text-brand-active">Results locked until Role Time Prioritization is completed</span>
              </div>
            )}
            {scenarioNum === 5 && (
              <div className="flex items-center gap-2">
                <Lock size={16} className="text-brand-active shrink-0" aria-hidden="true" />
                <span className="text-sm text-brand-active">Results locked until baseline is completed</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <div>
          {onBack && (
            <Button type="button" variant="outline" onClick={onBack} className="min-w-25 border-brand-steel/40">
              Back
            </Button>
          )}
        </div>
        <Button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={isLaunching}
          className="min-w-25 bg-brand-crimson hover:bg-brand-crimson/90 text-white font-medium text-sm rounded-lg px-6 py-2"
        >
          {isLaunching ? (
            <>
              <Loader2 size={16} className="animate-spin mr-2" aria-hidden="true" />
              Launching...
            </>
          ) : (
            "Launch Test"
          )}
        </Button>
      </div>

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-white rounded-xl shadow-2xl max-w-md border-brand-steel/50">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-gray-900">Launch Test</DialogTitle>
            <DialogDescription className="text-sm text-brand-steel">
              Are you sure you want to launch this test? Participants will be notified and the test will become active.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleLaunch}
              className="bg-brand-crimson hover:bg-brand-crimson/90 text-white font-medium text-sm rounded-lg px-4 py-2"
            >
              Confirm Launch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ReviewLaunchStep;
